import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BehaviorSubject, Subject } from 'rxjs';
import { NumberDirective } from 'src/app/shared';
import { FormValidator } from 'src/app/shared/utils/form-validator';

import { LoadingModule } from '../../../../../generals/loading/loading.module';
import { BusinessDirectionService } from '../../../../../services/business-direction.service';

@Component({
  selector: 'business-direction',
  templateUrl: './business-direction.component.html',
  styleUrls: ['./business-direction.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    FlexLayoutModule,
    MatInputModule,
    MatButtonModule,
    MatTooltipModule,
    MatSelectModule,
    MatIconModule,
    MatTableModule,
    LoadingModule,
    NumberDirective,
  ],
})
export class BusinessDirectionComponent implements OnInit, OnDestroy {
  @Input() businessData!: { businessUnitId: number; branchOfficeId: number };
  @Input() formData!: any;
  @Output() formValid = new EventEmitter<{
    status: boolean;
    form: FormGroup;
  }>();

  private fb = inject(FormBuilder);
  private fv = inject(FormValidator);
  private _snackBar = inject(MatSnackBar);
  private _unsubscribeAll!: Subject<any>;
  private businessDirectionService = inject(BusinessDirectionService);
  notification: any = {};
  form!: FormGroup;
  provinces: any[] = [];
  cantons: any[] = [];
  isEditing = false;

  private provincesSubject = new BehaviorSubject<any[]>([]);
  private cantonSubject = new BehaviorSubject<any[]>([]);

  ngOnInit(): void {
    this._unsubscribeAll = new Subject<any>();
    this.formInit();
    this.getProvinces();

    this.form.valueChanges.subscribe(() => {
      this.formValidate();
    });

    this.provincesSubject.subscribe(provinces => {
      const isValid =
        provinces.length > 0 &&
        this.formData &&
        this.formData.province !== undefined;

      if (isValid) {
        this.form.get('province')?.setValue(this.formData.province.toString());
        this.getCantons(this.formData.province);
      }
    });

    this.cantonSubject.subscribe(cantons => {
      if (!(cantons.length > 0 && this.formData)) {
        return;
      }

      if (!this.isEditing) {
        this.form.get('canton')?.setValue(this.formData.canton.toString());
        const { ...dataFiltrada } = this.formData;
        delete dataFiltrada.province;
        delete dataFiltrada.canton;
        this.form.patchValue(dataFiltrada);
      }

      this.isEditing = true;
    });
  }

  private formInit(): void {
    this.form = this.fb.group(
      {
        id: [null],
        branchOfficeId: [this.businessData.branchOfficeId],
        province: [null, Validators.required],
        canton: [null, Validators.required],
        street: [
          null,
          [
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(50),
            Validators.pattern(/^[a-zA-Z0-9\s]+$/),
          ],
        ],
        streetNumber: [
          null,
          [Validators.required, Validators.pattern(/^\d{1,5}$/)],
        ],
        intersection: [
          null,
          [
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(50),
          ],
        ],
        storeName: [
          null,
          [
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(50),
          ],
        ],
      },
      { updateOn: 'blur' },
    );
    this.startFormValidator();
  }

  getProvinces() {
    this.businessDirectionService.getProvinces().subscribe({
      next: (response: any) => {
        this.provinces = response;
        this.provincesSubject.next(this.provinces);
      },
    });
  }

  getCantons(id: number) {
    this.businessDirectionService.getCantonsByProvinceId(id).subscribe({
      next: (response: any) => {
        this.cantons = response;
        this.cantonSubject.next(this.cantons);
      },
    });
  }

  provinceSelect(event: MatSelectChange) {
    this.getCantons(event.value);
  }

  formValidate() {
    this.formValid.emit({ status: this.form.valid, form: this.form });
  }

  startFormValidator() {
    this.fieldNotificationInit();
  }

  private fieldNotificationInit(): void {
    const fields = {
      province: {
        required: 'Provincia es requerida',
      },
      canton: {
        required: 'Cantón es requerido',
      },
      street: {
        required: 'Calle es requerida',
        minlength: 'La calle debe tener al menos 3 caracteres',
        maxlength: 'La calle no puede tener más de 50 caracteres',
        pattern: 'La calle solo puede contener letras, números y espacios',
      },
      streetNumber: {
        required: 'Número de calle es requerido',
        pattern:
          'El número de calle debe contener solo dígitos y un máximo de 5 caracteres',
      },
      intersection: {
        required: 'Intersección es requerida',
        minlength: 'La intersección debe tener al menos 3 caracteres',
        maxlength: 'La intersección no puede tener más de 50 caracteres',
      },
      storeName: {
        required: 'Nombre del local es requerido',
        minlength: 'El nombre del local debe tener al menos 3 caracteres',
        maxlength: 'El nombre del local no puede tener más de 50 caracteres',
      },
    };

    this.fv.setFormValidator(this.form, this.notification, fields);
  }

  controlHasError(formControlName: string) {
    return this.fv.fieldHasError(formControlName);
  }

  private openSnack(message: string): void {
    this._snackBar.open(message, '', {
      verticalPosition: 'top',
      horizontalPosition: 'right',
      panelClass: ['snack-error'],
      duration: 5000,
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(undefined);
    this._unsubscribeAll.complete();
  }
}
